/**
 * Guides pédagogiques MPDS — Lot 3 : Traumatique + cas spéciaux
 */
export const buildTeachingGuidesLot3 = ({ Heart, AlertTriangle, ShieldAlert, Clock, Users }) => ({

  plaies: {
    title: 'Plaies / Blessures ouvertes',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `La gestion d\'une plaie passe avant tout par le contrôle de l\'hémorragie (compression directe). Les plaies souillées, profondes, sur articulations ou contenant des corps étrangers nécessitent toutes une évaluation médicale. Toujours évaluer le statut antitétanique et le mécanisme (verre, métal, animal).`,
    stats: [
      { icon: Heart, text: 'Les lacérations représentent 5 % des consultations en urgence.' },
      { icon: AlertTriangle, text: 'Le risque infectieux double si fermeture > 6 h après blessure.' },
      { icon: Clock, text: 'La compression directe stoppe 95 % des hémorragies de plaie.' },
    ],
    objectives: [
      'Évaluer la gravité (profondeur, longueur, localisation, hémorragie).',
      'Guider le contrôle d\'hémorragie par compression directe.',
      'Identifier les plaies à risque infectieux élevé (souillure, morsure).',
      'Préserver les structures avulsées pour réimplantation.',
      'Évaluer le statut antitétanique.',
    ],
    phases: [
      { n: 2, title: 'Caractéristiques de la plaie',
        why: 'Détermine urgence et conduite.',
        how: ['« Où est la plaie exactement ? »', '« Quelle taille — longueur, profondeur ? »', '« Saigne-t-elle encore activement ? »', '« Voyez-vous des structures (os, muscle, tendon) ? »'],
        pitfalls: ['Sous-estimer plaie au cuir chevelu (saignement abondant trompeur)'] },
      { n: 3, title: 'Mécanisme',
        why: 'Verre, métal rouillé, morsure changent le risque infectieux et la conduite.',
        how: ['« Comment c\'est arrivé ? »', '« Objet propre, sale, rouillé ? »', '« Y a-t-il un corps étranger encore dans la plaie ? »', '« A-t-il été mordu ? »'],
        pitfalls: ['Oublier de demander si tétanos à jour'] },
      { n: 4, title: 'État général et antécédents',
        why: 'Diabète, immunosuppression, anticoagulants modifient prise en charge.',
        how: ['« A-t-il des problèmes médicaux ? »', '« Prend-il des anticoagulants ? »', '« Quand son dernier vaccin antitétanique ? »'],
        pitfalls: ['Oublier la question des anticoagulants'] },
    ],
    citizen_behavior: {
      title: 'Guider les premiers soins d\'une plaie',
      tone: ['Voix rassurante — la vue du sang panique', 'Insister sur la compression continue', 'Différer le nettoyage si hémorragie active'],
      phrases_utiles: ['« Pressez FORT sur la plaie avec un linge propre. »', '« Ne lâchez pas, gardez la pression. »', '« Élevez le membre au-dessus du cœur. »', '« Si possible, gardez le corps étranger en place. »'],
      phrases_a_eviter: ['« Retirez le couteau / la branche » (peut aggraver hémorragie)', '« Versez de l\'eau de Javel sur la plaie »', '« Lavez bien fort avec savon » (en pleine hémorragie)'],
      panique_gestion: ['Si témoin a malaise vagal → s\'asseoir, jambes surélevées', 'Si enfant qui pleure → éloigner les autres enfants', 'Si plusieurs blessures → triage par hémorragie'],
    },
    red_flags: ['Saignement en jet pulsatile', 'Plaie > 5 cm de profondeur', 'Plaie à la tête, cou, thorax, abdomen', 'Plaie articulaire', 'Plaie avec corps étranger inamovible', 'Plaie par arme à feu', 'Plaie par animal (rage)', 'Plaie avec atteinte vasculaire ou nerveuse', 'Plaie souillée + tétanos non à jour', 'Plaie chez patient sous anticoagulants'],
    pre_arrival: [
      'Compression directe ferme et continue',
      'Élever le membre si possible',
      'NE PAS retirer corps étranger pénétrant',
      'NE PAS appliquer de produits irritants',
      'Couvrir avec linge propre',
      'Conserver tissus avulsés (sac propre humide dans sac avec glace)',
      'Allonger jambes surélevées si signes de choc',
      'Maintenir au chaud (couverture)',
    ],
    common_errors: [
      'Retirer un objet pénétrant',
      'Vouloir nettoyer en pleine hémorragie',
      'Sous-estimer plaie au cuir chevelu',
      'Oublier la question du tétanos',
    ],
    discussion_questions: [
      'Travailleur empalé par une barre de fer dans la cuisse. Conduite ?',
      'Enfant avec plaie de 3 cm au front, saigne abondamment. Urgence ?',
      'Patient diabétique avec plaie au pied marchant dessus. Risques ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 21 — Hemorrhage/Lacerations',
      'CDC — Tetanus immunization guidelines',
      'WHO — Wound care principles',
    ],
  },

  brulures: {
    title: 'Brûlures',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Les brûlures se classent par degré (1er, 2e, 3e) et par surface corporelle atteinte. Le seuil critique : > 10 % de surface chez l\'adulte, > 5 % chez l\'enfant, ou brûlures à zones critiques (visage, mains, périnée, articulations, voies aériennes). Refroidir l\'eau froide 20 minutes reste le geste #1.`,
    stats: [
      { icon: Heart, text: '~20 000 brûlures traitées en urgence/an au Canada.' },
      { icon: AlertTriangle, text: 'Les brûlures inhalation tuent par œdème laryngé en quelques heures.' },
      { icon: Clock, text: 'Refroidir 20 min réduit la profondeur de la brûlure de 1 degré.' },
    ],
    objectives: [
      'Évaluer la surface corporelle brûlée (règle des 9, paume = 1 %).',
      'Identifier les brûlures à risque vital (voies aériennes, électriques, chimiques).',
      'Guider le refroidissement à l\'eau froide 20 min.',
      'NE PAS appliquer pommade, glace ou tissu adhérent.',
      'Prévenir l\'hypothermie chez le grand brûlé.',
    ],
    phases: [
      { n: 2, title: 'Cause et lieu',
        why: 'Chimique, électrique, thermique, vapeur → conduites différentes.',
        how: ['« Qu\'est-ce qui a causé la brûlure ? »', '« Liquide chaud, flamme, électricité, produit chimique ? »', '« Brûlure dans un espace clos (fumée) ? »', '« Le feu est-il éteint maintenant ? »'],
        pitfalls: ['Oublier de demander si fumée respirée'] },
      { n: 3, title: 'Zone et étendue',
        why: 'Surface brûlée détermine l\'urgence absolue.',
        how: ['« Quelle partie du corps ? »', '« Combien de paumes de main de surface environ ? »', '« Visage, mains, parties génitales ? »', '« Voyez-vous des cloques (2e degré) ou des zones blanches/noires (3e degré) ? »'],
        pitfalls: ['Confondre rougeur 1er degré (bénin) et 2e degré (grave si étendu)'] },
      { n: 4, title: 'Voies aériennes',
        why: 'Brûlure des voies aériennes = urgence #1 (œdème en quelques heures).',
        how: ['« A-t-il respiré de la fumée ou de la vapeur chaude ? »', '« Voix rauque, toux, sifflement ? »', '« Suie autour du nez ou de la bouche ? »', '« Poils du nez ou cils brûlés ? »'],
        pitfalls: ['Sous-estimer brûlure VA si pas encore symptomatique'] },
    ],
    citizen_behavior: {
      title: 'Premiers soins d\'une brûlure',
      tone: ['Voix calme — le patient peut être en douleur intense', 'Instructions très précises sur durée du refroidissement', 'Rassurer sur la suite'],
      phrases_utiles: ['« Refroidissez à l\'eau froide du robinet pendant 20 minutes. »', '« Retirez bijoux et vêtements NON adhérents. »', '« Couvrez ensuite d\'un linge propre humide. »'],
      phrases_a_eviter: ['« Mettez de la glace » (aggrave la lésion)', '« Appliquez du beurre / dentifrice »', '« Crevez les cloques »', '« Retirez les vêtements adhérents »'],
      panique_gestion: ['Si grand brûlé → couvrir d\'une couverture sèche après refroidissement (hypothermie)', 'Si feu sur la personne → "stop, drop & roll"', 'Si enfant → demander à un adulte de gérer le bain froid'],
    },
    red_flags: ['Brûlure > 10 % surface (adulte) / > 5 % (enfant)', 'Brûlure 3e degré', 'Brûlure visage / cou / voies aériennes', 'Brûlure mains, pieds, parties génitales, articulations', 'Brûlure électrique (haute tension)', 'Brûlure chimique', 'Brûlure circulaire (membre, thorax)', 'Suie au visage', 'Inhalation de fumée', 'Patient âgé ou nourrisson', 'Antécédent cardiaque'],
    pre_arrival: [
      'Eau froide du robinet 20 minutes (pas glacée)',
      'Retirer bijoux, montres, ceintures (œdème à venir)',
      'NE PAS retirer vêtements adhérents',
      'NE PAS percer cloques',
      'NE PAS appliquer pommade, beurre, dentifrice',
      'Couvrir d\'un linge propre humide ou pellicule plastique',
      'Couvrir d\'une couverture sèche pour éviter hypothermie',
      'Surélever membres atteints',
      'Si brûlure chimique → rinçage abondant 20-30 min',
    ],
    common_errors: [
      'Appliquer glace (aggrave)',
      'Remèdes de grand-mère (beurre, dentifrice)',
      'Refroidir < 5 min (inefficace)',
      'Refroidir grand brûlé > 10 min (hypothermie)',
      'Crever les cloques',
    ],
    discussion_questions: [
      'Enfant qui s\'est échaudé avec eau bouillante sur 15 % du corps. Conduite ?',
      'Femme retirée d\'un incendie, toux et voix rauque. Risques ?',
      'Travailleur brûlé par produit chimique au visage. Premiers gestes ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 7 — Burns',
      'EMSB — Emergency Management of Severe Burns',
      'Fondation des grands brûlés du Québec',
    ],
  },

  chute: {
    title: 'Chute de hauteur',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Une chute de plus de 3 mètres (ou 3× la taille du patient pour un enfant) est considérée comme à haut risque traumatique : lésions rachidiennes, abdominales, crâniennes. Le mot d\'ordre : ne PAS mobiliser la victime — immobilisation cervicale jusqu\'à l\'arrivée des secours.`,
    stats: [
      { icon: Heart, text: 'Les chutes sont la 1re cause de blessures involontaires au Canada.' },
      { icon: AlertTriangle, text: 'Chute > 3 m chez adulte = trauma sévère dans 30 % des cas.' },
      { icon: Clock, text: 'Lésion rachidienne complète irréversible si manipulation inadéquate.' },
    ],
    objectives: [
      'Évaluer la hauteur et le mécanisme de la chute.',
      'Suspecter une lésion rachidienne et prévenir toute mobilisation.',
      'Évaluer le niveau de conscience et signes neurologiques.',
      'Identifier les surfaces d\'impact (béton vs gazon vs eau).',
      'Coordonner avec pompiers si extraction difficile.',
    ],
    phases: [
      { n: 2, title: 'Hauteur et surface',
        why: 'Détermine cinétique et probabilité de lésions sévères.',
        how: ['« De quelle hauteur est-il tombé (mètres ou étages) ? »', '« Sur quelle surface est-il tombé ? »', '« Comment est-il tombé — sur les pieds, sur le dos, la tête ? »'],
        pitfalls: ['Confondre 1 étage / 2 étages — bien préciser'] },
      { n: 3, title: 'État neurologique',
        why: 'Conscience + déficit = pronostic.',
        how: ['« Est-il conscient ? Réagit-il à la voix ? »', '« Bouge-t-il ses bras et jambes ? »', '« Sent-il vos doigts si vous touchez ses orteils ? »', '« Y a-t-il du sang ou liquide clair par l\'oreille ou le nez ? »'],
        pitfalls: ['Demander de mobiliser pour tester (CONTRE-INDIQUÉ)'] },
      { n: 4, title: 'Position et lésions visibles',
        why: 'Évalue accessibilité, scoliose post-chute, déformations.',
        how: ['« Sur quel côté est-il ? »', '« Voyez-vous une jambe ou un bras dans une position anormale ? »', '« Saignements visibles ? »'],
        pitfalls: ['Faire tourner pour mieux voir'] },
    ],
    citizen_behavior: {
      title: 'Empêcher la mobilisation',
      tone: ['Voix très ferme sur l\'immobilisation', 'Expliquer le danger sans alarmer', 'Donner consignes claires pour stabiliser'],
      phrases_utiles: ['« Ne le bougez PAS — sa colonne pourrait être fracturée. »', '« Maintenez sa tête entre vos deux mains, dans l\'axe du corps. »', '« Parlez-lui pour qu\'il reste conscient et immobile. »'],
      phrases_a_eviter: ['« Essayez de le faire asseoir »', '« Tournez-le sur le côté » (sauf vomissements)', '« Remettez-le en position »'],
      panique_gestion: ['Si témoin veut "secouer" la victime → directive ferme : NON', 'Si plusieurs témoins → 1 personne tient la tête, autres surveillent', 'Si patient veut se relever → le convaincre de ne PAS bouger'],
    },
    red_flags: ['Chute > 3 m (adulte) ou > 1,5 m (enfant)', 'Surface dure (béton, métal)', 'Chute tête première', 'Inconscience', 'Convulsions post-chute', 'Déficit moteur / sensitif', 'Saignement / liquide clair par oreille ou nez', 'Vomissements', 'Confusion progressive', 'Difficulté à respirer'],
    pre_arrival: [
      'NE PAS mobiliser la victime',
      'Stabiliser la tête manuellement dans l\'axe du corps',
      'Maintenir au chaud (couverture)',
      'NE PAS donner à boire ou manger',
      'Surveiller respiration et conscience',
      'Si vomissements → tourner en bloc (tête + corps ensemble) avec aide',
      'Si pas de conscience et pas de respiration → RCR (priorité vitale > rachis)',
      'Préparer l\'accès pour ambulanciers',
    ],
    common_errors: [
      'Faire asseoir ou marcher la victime',
      'Tourner pour examiner sans précaution rachidienne',
      'Sous-estimer une chute de sa propre hauteur chez âgé',
      'Oublier la stabilisation cervicale',
    ],
    discussion_questions: [
      'Ouvrier tombé du 2e étage d\'un échafaudage, inconscient. Action ?',
      'Enfant tombé d\'un module de jeu, conscient mais pleure et ne bouge pas la jambe. Conduite ?',
      'Personne âgée tombée de sa hauteur, hanche douloureuse, ne peut se relever. Que faire ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 17 — Falls',
      'ATLS — Advanced Trauma Life Support',
      'PHTLS — Prehospital Trauma Life Support',
    ],
  },

  trauma_cranien: {
    title: 'Traumatisme Crânien',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Le traumatisme crânien (TC) regroupe des lésions allant de la commotion bénigne à l\'hématome épidural mortel en 4-6 h. La perte de conscience initiale, l\'amnésie, les vomissements et les déficits neurologiques sont les principaux signes d\'alarme. Tout TC sous anticoagulant est une urgence absolue.`,
    stats: [
      { icon: Heart, text: 'Le TC est la 1re cause de décès chez les < 45 ans au Canada.' },
      { icon: AlertTriangle, text: 'L\'hématome épidural a un intervalle libre de quelques heures avant détérioration brutale.' },
      { icon: Clock, text: 'Patient sous anticoagulant : risque hémorragique x10.' },
    ],
    objectives: [
      'Évaluer la cinétique du traumatisme.',
      'Identifier les drapeaux rouges (Glasgow ↓, vomissements, anisocorie).',
      'Suspecter trauma rachidien associé.',
      'Reconnaître l\'intervalle libre des hématomes intracrâniens.',
      'Identifier les patients à très haut risque (anticoagulants, âgés).',
    ],
    phases: [
      { n: 2, title: 'Mécanisme et impact',
        why: 'Cinétique = pronostic.',
        how: ['« Comment s\'est-il cogné — chute, accident, agression ? »', '« Quelle vitesse / hauteur ? »', '« A-t-il porté un casque ? »', '« A-t-il perdu connaissance ? Combien de temps ? »'],
        pitfalls: ['Sous-estimer le coup "léger" à la tête'] },
      { n: 3, title: 'Symptômes actuels',
        why: 'Évolution = signe d\'aggravation intracrânienne.',
        how: ['« A-t-il vomi ? Combien de fois ? »', '« Confusion, propos incohérents ? »', '« Maux de tête qui empirent ? »', '« Vision floue, double ? »', '« Convulsions ? »', '« Sang ou liquide clair par l\'oreille ou le nez ? »'],
        pitfalls: ['Confondre intoxication alcoolique et trauma'] },
      { n: 4, title: 'Antécédents et médication',
        why: 'Anticoagulants = trauma crânien = scan d\'office.',
        how: ['« Prend-il des anticoagulants (Coumadin, Eliquis, Xarelto, Pradaxa) ? »', '« A-t-il des antécédents d\'AVC, chirurgie cérébrale ? »'],
        pitfalls: ['Oublier la question des anticoagulants'] },
    ],
    citizen_behavior: {
      title: 'Surveiller activement un trauma crânien',
      tone: ['Voix calme et structurée', 'Demander de tenir un journal de surveillance', 'Réveiller la victime régulièrement'],
      phrases_utiles: ['« Restez près de lui, parlez-lui régulièrement. »', '« S\'il s\'endort, réveillez-le toutes les heures. »', '« Notez tout changement de comportement. »'],
      phrases_a_eviter: ['« Laissez-le dormir, ça lui fera du bien »', '« Donnez-lui de l\'aspirine pour le mal de tête » (aggrave hémorragie)', '« Mettez de la glace » (chez < 5 % patient, pas grave si pas hématome)'],
      panique_gestion: ['Si parent panique pour bébé tombé → questionnement structuré', 'Si patient ne se souvient plus → c\'est normal après TC, rassurer', 'Si conjoint ne sait pas évaluer → questions simples comme prénom, date'],
    },
    red_flags: ['Perte de conscience > 1 min', 'Amnésie persistante', 'Vomissements répétés', 'Confusion progressive', 'Anisocorie (pupilles inégales)', 'Convulsions', 'Sang ou liquide clair par oreille/nez', 'Hématome rétroauriculaire (Battle\'s sign)', 'Yeux de raton-laveur (panda eyes)', 'Patient âgé', 'Anticoagulants', 'Chute > 1 m'],
    pre_arrival: [
      'NE PAS mobiliser si chute (suspicion rachis)',
      'Position semi-assise SI pas trauma rachis suspecté',
      'NE PAS donner d\'aspirine (aggrave hémorragie)',
      'NE PAS donner à boire ou manger',
      'Surveiller niveau de conscience régulièrement',
      'Glace enveloppée d\'un linge sur la zone (si pas hématome qui s\'étend)',
      'Réveiller toutes les heures les premières 12 h',
      'Si conscience baisse → 911 retour, position latérale de sécurité',
    ],
    common_errors: [
      'Laisser dormir sans réveiller',
      'Donner aspirine ou ibuprofène',
      'Sous-estimer trauma sous anticoagulants',
      'Oublier la stabilisation cervicale',
    ],
    discussion_questions: [
      'Hockeyeur 16 ans après commotion, vomit 2 h plus tard. Urgence ?',
      'Patient âgé sous Coumadin, chute de sa hauteur, semble bien. Faut-il transporter ?',
      'Bébé 8 mois tombé d\'un changement de table, pleure puis s\'endort. Action ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 30 — Traumatic Injuries',
      'Canadian CT Head Rule — Stiell IG 2001',
      'Brain Injury Canada — Lignes directrices',
    ],
  },

  trauma_penetrant: {
    title: 'Trauma Pénétrant (arme blanche / projectile)',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Les plaies par arme blanche ou projectile peuvent paraître bénignes à l\'extérieur tout en ayant détruit des organes vitaux. La règle d\'or : NE JAMAIS retirer l\'objet pénétrant — il fait souvent tampon sur les vaisseaux sectionnés. Sécurité de la scène = priorité absolue (agresseur encore présent ?).`,
    stats: [
      { icon: Heart, text: 'Au Canada, ~250 décès/an par arme à feu, ~150 par arme blanche.' },
      { icon: AlertTriangle, text: 'Une plaie thoracique pénétrante peut causer pneumothorax sous tension en quelques minutes.' },
      { icon: Clock, text: 'Le temps avant chirurgie est le facteur principal de survie.' },
    ],
    objectives: [
      'Sécuriser la scène AVANT toute intervention.',
      'NE JAMAIS retirer un objet pénétrant.',
      'Reconnaître le pneumothorax ouvert (plaie thoracique soufflante).',
      'Identifier les zones critiques (cou, thorax, abdomen, aine).',
      'Coordonner avec services policiers.',
    ],
    phases: [
      { n: 2, title: 'Sécurité de la scène',
        why: 'Priorité absolue — éviter une 2e victime.',
        how: ['« L\'agresseur est-il encore là ? »', '« Êtes-vous en sécurité actuellement ? »', '« Y a-t-il une arme visible ? »', '« Combien de victimes ? »'],
        pitfalls: ['Faire intervenir avant police si scène non sécurisée'] },
      { n: 3, title: 'Localisation et objet',
        why: 'Détermine gravité et conduite.',
        how: ['« Où exactement sur le corps ? »', '« Par quel type d\'objet — couteau, arme à feu, perçage ? »', '« L\'objet est-il encore en place ? »', '« Une seule blessure ou plusieurs ? »'],
        pitfalls: ['Oublier de chercher d\'autres plaies'] },
      { n: 4, title: 'État de la victime',
        why: 'Évalue choc hémorragique.',
        how: ['« Est-il conscient ? »', '« Respire-t-il normalement ? »', '« Saigne-t-il abondamment ? »', '« Est-il pâle, en sueurs, agité ? »'],
        pitfalls: ['Sous-estimer l\'hémorragie interne (peu visible)'] },
    ],
    citizen_behavior: {
      title: 'Gérer une scène traumatique violente',
      tone: ['Voix très calme — le témoin peut être choc', 'Donner instructions très simples', 'Ne pas demander de récit de l\'agression'],
      phrases_utiles: ['« Pressez fort autour de l\'objet, mais ne le retirez PAS. »', '« Si plaie au thorax qui souffle → couvrez avec votre main sans appuyer, 3 côtés scotchés. »', '« Allongez-le, je reste avec vous. »'],
      phrases_a_eviter: ['« Retirez le couteau » (TRÈS DANGEREUX)', '« Faites-le marcher »', '« Donnez-lui à boire »'],
      panique_gestion: ['Si témoin a participé à la scène → ne pas juger, focaliser action', 'Si témoin pleure → respirations guidées rapides', 'Si scène conjugale → questionner discrètement sur sécurité'],
    },
    red_flags: ['Plaie au cou', 'Plaie thoracique soufflante', 'Plaie abdominale avec viscères extériorisés', 'Hémorragie massive', 'Inconscience', 'Pâleur extrême + tachycardie', 'Difficulté à respirer', 'Trauma à l\'aine (artère fémorale)', 'Plusieurs plaies', 'Arme à feu (lésions à distance)', 'Agresseur en fuite (police obligatoire)'],
    pre_arrival: [
      'Sécuriser la scène — police OBLIGATOIRE',
      'NE PAS retirer l\'objet pénétrant',
      'Stabiliser l\'objet (rouleaux de tissus autour pour éviter mouvement)',
      'Compression autour de la plaie, sur les côtés',
      'Plaie thoracique soufflante → pansement scotché 3 côtés (pneumothorax)',
      'Plaie abdominale avec viscères → couvrir avec linge humide propre, ne pas remettre',
      'Allonger, jambes surélevées si choc',
      'Couvrir d\'une couverture',
      'NE RIEN donner par la bouche',
    ],
    common_errors: [
      'Retirer l\'objet pénétrant',
      'Sous-estimer une "petite" plaie pénétrante',
      'Ne pas suspecter pneumothorax sous tension',
      'Approcher avant sécurisation de la scène',
    ],
    discussion_questions: [
      'Victime poignardée au thorax, plaie soufflante. Conduite pré-arrivée ?',
      'Travailleur empalé sur barre de fer, scène sécurisée. Que dire au témoin ?',
      'Plaie par balle au cou. Quels sont les risques immédiats ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 27 — Stab/Gunshot',
      'TCCC — Tactical Combat Casualty Care',
      'Trauma Association of Canada',
    ],
  },

  accident_routier: {
    title: 'Accident Routier (polytraumatisé)',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'accident de la route est l\'archétype du polytraumatisme : associations crâniennes, rachidiennes, thoraciques, abdominales. La cinétique (vitesse, type d\'impact, projection, déformation du véhicule) prédit la gravité plus que les plaintes initiales du patient — l\'effet d\'adrénaline masque souvent les lésions sévères.`,
    stats: [
      { icon: Heart, text: '~1900 décès/an sur les routes au Canada.' },
      { icon: AlertTriangle, text: '40 % des décès surviennent dans la première heure (golden hour).' },
      { icon: Clock, text: 'Le temps d\'extraction influence directement la survie.' },
    ],
    objectives: [
      'Évaluer la cinétique (vitesse, type d\'impact, projection).',
      'Sécuriser la scène (circulation, feu, fuites essence).',
      'Identifier les blessés graves cachés par adrénaline.',
      'NE PAS mobiliser sans précaution rachidienne sauf danger immédiat.',
      'Coordonner avec pompiers (extraction, désincarcération).',
    ],
    phases: [
      { n: 2, title: 'Sécurité de la scène',
        why: 'Empêche les sur-accidents.',
        how: ['« Êtes-vous en sécurité actuellement ? »', '« Sortez du véhicule si possible et sécuritaire. »', '« Y a-t-il de la fumée, du feu, des fuites ? »', '« Y a-t-il du trafic dangereux ? »', '« Allumez vos feux de détresse. »'],
        pitfalls: ['Faire descendre un blessé sans support si suspicion rachis'] },
      { n: 3, title: 'Nombre de victimes et état',
        why: 'Active triage et ressources adéquates.',
        how: ['« Combien de véhicules impliqués ? »', '« Combien de victimes ? »', '« Y a-t-il des personnes coincées ? »', '« Y a-t-il des piétons, cyclistes, motocyclistes ? »', '« Quelqu\'un est-il éjecté du véhicule ? »'],
        pitfalls: ['Sous-estimer le nombre de victimes (motoneige, piéton non vu)'] },
      { n: 4, title: 'Cinétique',
        why: 'Prédit la sévérité.',
        how: ['« À quelle vitesse approximative ? »', '« Type d\'impact — frontal, latéral, arrière, retournement ? »', '« Le véhicule est-il très déformé ? »', '« Airbags déployés ? »', '« Ceintures attachées ? »'],
        pitfalls: ['Confondre vitesse perçue et réelle'] },
    ],
    citizen_behavior: {
      title: 'Encadrer une scène d\'accident chaotique',
      tone: ['Voix structurée et directive', 'Donner instructions une à la fois', 'Préparer arrivée des secours'],
      phrases_utiles: ['« Restez dans votre véhicule si vous êtes en sécurité. »', '« Allumez feux de détresse et appelez d\'autres conducteurs à ralentir. »', '« Ne bougez PAS les blessés sauf danger immédiat. »', '« Si quelqu\'un est inconscient et ne respire pas, je vous guide pour la RCR. »'],
      phrases_a_eviter: ['« Sortez les blessés du véhicule » (sauf feu)', '« Détachez la ceinture pour le confort »', '« Donnez à boire »'],
      panique_gestion: ['Si plusieurs témoins → désigner UN coordonnateur', 'Si témoin a peur de bouger le blessé → confirmer que c\'est bien de ne pas le faire', 'Si enfant impliqué → ses parents souvent paniquent, voix ultra calme'],
    },
    red_flags: ['Éjection du véhicule', 'Décès d\'un occupant', 'Désincarcération nécessaire', 'Retournement', 'Impact frontal à haute vitesse', 'Piéton / cycliste / motocycliste', 'Patient inconscient', 'Détresse respiratoire', 'Hémorragie massive visible', 'Trouble de conscience progressif', 'Enfant non attaché correctement', 'Femme enceinte impliquée'],
    pre_arrival: [
      'Sécuriser la scène (feux détresse, balises, triangle si possible)',
      'NE PAS mobiliser les blessés sauf danger imminent (feu, eau)',
      'Stabiliser tête des blessés conscients dans le véhicule',
      'Couper le contact (sauf si déclenche les airbags non déployés)',
      'Surveiller respiration des inconscients',
      'Compression des hémorragies actives',
      'Couvrir les victimes (couvertures)',
      'NE PAS retirer casque de motocycliste si conscient et respirant',
      'Compter les victimes pour secours',
    ],
    common_errors: [
      'Sortir les blessés sans précaution rachidienne',
      'Sous-estimer le polytraumatisé masqué par adrénaline',
      'Oublier les piétons / cyclistes / animaux impliqués',
      'Ne pas sécuriser la scène avant intervention',
    ],
    discussion_questions: [
      'Accident frontal à 100 km/h, 2 véhicules, 5 occupants. Comment trier ?',
      'Motocycliste conscient mais immobile au sol. Faut-il retirer le casque ?',
      'Femme enceinte 8 mois impliquée dans collision arrière. Priorité ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 29 — Traffic Accidents',
      'ATLS — Advanced Trauma Life Support',
      'SAAQ — Bilan routier annuel',
    ],
  },

  fractures: {
    title: 'Fractures / Luxations',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Les fractures peuvent être bénignes (fissure d\'un orteil) ou critiques (fracture de fémur avec hémorragie cachée, fracture du bassin avec choc, fracture ouverte avec risque infectieux). L\'immobilisation et la prévention du choc sont les deux piliers de la gestion pré-hospitalière.`,
    stats: [
      { icon: Heart, text: 'La fracture du fémur peut entraîner perte de 1-1,5 L de sang interne.' },
      { icon: AlertTriangle, text: 'La fracture du bassin peut causer 3-5 L de perte sanguine.' },
      { icon: Clock, text: 'Réduction d\'une luxation < 6 h améliore le pronostic vasculaire.' },
    ],
    objectives: [
      'Différencier fracture fermée vs ouverte.',
      'Identifier les fractures à risque vital (fémur, bassin, cervicale).',
      'Évaluer la perfusion distale (couleur, sensibilité, mouvement).',
      'Immobiliser en position trouvée.',
      'Ne JAMAIS tenter de réduire une luxation à distance.',
    ],
    phases: [
      { n: 2, title: 'Localisation et type',
        why: 'Détermine gravité immédiate.',
        how: ['« Quelle partie du corps ? »', '« Y a-t-il une déformation visible ? »', '« Os apparent à travers la peau (fracture ouverte) ? »', '« Saignement actif ? »'],
        pitfalls: ['Confondre déformation et entorse'] },
      { n: 3, title: 'Mécanisme',
        why: 'Cinétique = sévérité.',
        how: ['« Comment c\'est arrivé ? »', '« Chute de quelle hauteur ? »', '« Force d\'impact ? »', '« Sport, accident, chute simple ? »'],
        pitfalls: ['Sous-estimer mécanisme à faible énergie chez âgé'] },
      { n: 4, title: 'Perfusion distale',
        why: 'Une fracture peut comprimer artère/nerf.',
        how: ['« Sent-il ses doigts ou orteils ? »', '« Peut-il les bouger ? »', '« Sont-ils chauds et de couleur normale ? »', '« Voyez-vous un pouls / battement distal ? »'],
        pitfalls: ['Oublier d\'évaluer la perfusion distale'] },
    ],
    citizen_behavior: {
      title: 'Soulager douleur sans mobiliser',
      tone: ['Voix rassurante — fractures très douloureuses', 'Insister sur immobilisation', 'Préparer position confortable'],
      phrases_utiles: ['« Ne le bougez pas, on va l\'immobiliser tel qu\'il est. »', '« Mettez un linge plié sous le membre pour le caler. »', '« Glace enveloppée d\'un linge, jamais en contact direct. »'],
      phrases_a_eviter: ['« Remettez l\'os en place » (TRÈS DANGEREUX)', '« Marchez pour voir si ça va »', '« Donnez-lui à boire en cas de chirurgie »'],
      panique_gestion: ['Si fracture ouverte → couvrir d\'un linge propre sans pousser l\'os', 'Si déformation impressionnante → ne pas alarmer la victime', 'Si enfant → distraire pour réduire l\'anxiété'],
    },
    red_flags: ['Fracture ouverte (os apparent)', 'Fracture du fémur (bilatérale = choc certain)', 'Fracture du bassin', 'Fracture cervicale ou rachidienne', 'Perte de pouls / sensibilité distale', 'Pâleur du membre', 'Patient en état de choc', 'Multiples fractures', 'Patient sous anticoagulants', 'Suspicion fracture crâne', 'Fracture côtes avec dyspnée'],
    pre_arrival: [
      'Immobiliser en position trouvée',
      'NE PAS tenter de réduire',
      'Caler avec coussins, vêtements pliés',
      'Glace enveloppée d\'un linge (jamais directement sur la peau)',
      'Si fracture ouverte → couvrir avec linge propre sans pousser l\'os',
      'Surveiller pouls et sensibilité distale',
      'Allonger jambes surélevées si signes de choc',
      'NE PAS donner à boire (anesthésie possible)',
      'Couvrir d\'une couverture',
    ],
    common_errors: [
      'Vouloir « remettre en place »',
      'Sous-estimer une fracture de fémur (choc imminent)',
      'Ne pas évaluer pouls et sensibilité distale',
      'Donner à boire au patient (NPO chirurgie)',
    ],
    discussion_questions: [
      'Patient âgé tombé, hanche douloureuse, jambe en rotation externe. Suspicion ?',
      'Enfant tombé avec déformation visible avant-bras. Conduite ?',
      'Travailleur écrasé avec suspicion fracture du bassin. Pourquoi est-ce critique ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 17 — Falls / Trauma',
      'AAOS — Orthopedic Trauma Guidelines',
      'PHTLS — Prehospital Trauma Life Support',
    ],
  },

  agression: {
    title: 'Agression / Voies de fait',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'appel pour agression cumule plusieurs dimensions : médicale (blessures), psychologique (trauma), légale (préservation de preuves), policière (sécurité scène). Le répartiteur doit jongler entre soins urgents, sécurité de l\'appelant et coordination avec les forces de l\'ordre. L\'agresseur peut être encore présent — sécurité avant tout.`,
    stats: [
      { icon: Heart, text: 'Violence conjugale : 1 femme sur 3, 1 homme sur 6 affectés au Canada.' },
      { icon: AlertTriangle, text: '60 % des décès par homicide impliquent un proche.' },
      { icon: Clock, text: 'Les 72 premières heures après l\'agression sont critiques pour la preuve médico-légale.' },
    ],
    objectives: [
      'Sécuriser l\'appelant AVANT toute autre action.',
      'Évaluer les blessures sans déplacer la victime si trauma majeur.',
      'Préserver les preuves médico-légales (ne pas se laver, ne pas changer de vêtements).',
      'Coordonner police + ambulance + soutien psychologique.',
      'Identifier les contextes à haut risque (violence conjugale, agression sexuelle).',
    ],
    phases: [
      { n: 2, title: 'Sécurité de l\'appelant',
        why: 'L\'agresseur peut être encore présent.',
        how: ['« Êtes-vous en sécurité actuellement ? »', '« L\'agresseur est-il toujours là ? »', '« Pouvez-vous vous enfermer dans une pièce avec votre téléphone ? »', '« Si oui → restez en ligne, je reste avec vous. »', '« Êtes-vous armé(e) ? »'],
        pitfalls: ['Faire de longs questionnements si appelante en danger immédiat'] },
      { n: 3, title: 'Nature des blessures',
        why: 'Triage médical.',
        how: ['« Où êtes-vous blessé(e) ? »', '« Y a-t-il du sang ? »', '« Avez-vous perdu connaissance ? »', '« Pouvez-vous bouger normalement ? »', '« Quelqu\'un d\'autre est blessé ? »'],
        pitfalls: ['Sous-estimer trauma psychologique'] },
      { n: 4, title: 'Contexte et préservation',
        why: 'Aspect médico-légal.',
        how: ['« Connaissez-vous l\'agresseur ? »', '« A-t-il une arme ? »', '« Si agression sexuelle : NE vous lavez pas, NE changez pas de vêtements. »', '« Conservez tout — vêtements, draps. »'],
        pitfalls: ['Pression pour identifier l\'agresseur (la police le fera)'] },
    ],
    citizen_behavior: {
      title: 'Soutenir une victime traumatisée',
      tone: ['Voix très douce et lente', 'Pas de jugement, pas d\'analyse', 'Validation : "ce n\'est pas votre faute"'],
      phrases_utiles: ['« Je vous crois, je suis avec vous. »', '« Ce n\'est pas votre faute. »', '« Vous êtes en sécurité maintenant, l\'aide arrive. »', '« Vous n\'êtes pas obligé(e) de tout raconter maintenant. »'],
      phrases_a_eviter: ['« Pourquoi vous êtes restée avec lui ? »', '« Qu\'est-ce que vous avez fait pour provoquer ça ? »', '« Avez-vous porté plainte avant ? »'],
      panique_gestion: ['Si victime pleure → respect du silence, présence vocale', 'Si victime minimise → ne pas insister, simplement noter', 'Si enfant impliqué → questions à un adulte de confiance, pas à l\'enfant'],
    },
    red_flags: ['Perte de conscience', 'Saignement actif', 'Trauma crânien', 'Blessures par arme blanche / à feu', 'Strangulation (même brève)', 'Agression sexuelle', 'Victime enceinte', 'Enfants présents', 'Agresseur toujours sur les lieux', 'Antécédent de violence conjugale', 'Menaces de mort proférées', 'Présence d\'arme'],
    pre_arrival: [
      'Sécuriser l\'appelant (enfermer, fuir, se cacher)',
      'Rester EN LIGNE jusqu\'à arrivée des secours',
      'Soins immédiats : compression des hémorragies',
      'NE PAS se laver / changer si agression sexuelle',
      'NE PAS toucher la scène (préservation preuves)',
      'Récupérer les enfants en sécurité',
      'Préparer documents (carte santé, ordonnance d\'éloignement)',
      'Pas de questions sur les détails, focus sur sécurité',
    ],
    common_errors: [
      'Questionner sur les détails de l\'agression',
      'Conseiller de se laver / changer',
      'Sous-estimer la strangulation (lésion possible des voies aériennes)',
      'Ne pas mobiliser les ressources d\'aide psychologique',
    ],
    discussion_questions: [
      'Femme appelle à voix basse, mari présent, dit "je suis tombée". Conduite ?',
      'Agression sexuelle il y a 2 h. Que dire à la victime concernant l\'hygiène ?',
      'Enfant qui décrit des blessures non accidentelles. Devoir de signalement ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 4 — Assault/Sexual Assault',
      'CALACS — Centres d\'aide et de lutte contre les agressions à caractère sexuel',
      'SOS Violence conjugale : 1-800-363-9010',
    ],
  },

  etouffement: {
    title: 'Étouffement / Obstruction des Voies Aériennes',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'étouffement par corps étranger tue en 4-6 minutes par hypoxie cérébrale. La distinction critique : obstruction partielle (la victime tousse, respire bruyamment) ne nécessite pas de manœuvre — l\'encourager à tousser. Obstruction complète (silence, pas d\'air) = Heimlich immédiat.`,
    stats: [
      { icon: Heart, text: '~3 000 décès par étouffement/an au Canada.' },
      { icon: AlertTriangle, text: 'L\'étouffement est la 4e cause de décès chez < 1 an.' },
      { icon: Clock, text: 'Hypoxie cérébrale irréversible après 4-6 minutes.' },
    ],
    objectives: [
      'Différencier obstruction partielle vs complète.',
      'Guider la manœuvre de Heimlich chez l\'adulte/enfant.',
      'Adapter chez le nourrisson (5 tapes dorsales + 5 compressions thoraciques).',
      'Initier la RCR si la victime devient inconsciente.',
      'Reconnaître les corps étrangers fréquents (nourriture, jouets, dentier).',
    ],
    phases: [
      { n: 2, title: 'Type d\'obstruction',
        why: 'Partielle vs complète change tout.',
        how: ['« Peut-il tousser, parler, faire du bruit ? »', '« Si oui (obstruction partielle) → encouragez-le à tousser fort »', '« Si non (silence, lèvres bleues) → manœuvre de Heimlich IMMÉDIATE »'],
        pitfalls: ['Pratiquer Heimlich sur obstruction partielle (peut empirer)'] },
      { n: 3, title: 'Âge de la victime',
        why: 'Adulte/enfant > 1 an = Heimlich ; bébé = tapes dorsales.',
        how: ['« Quel âge a la victime ? »', '« Plus ou moins de 1 an ? »'],
        pitfalls: ['Heimlich sur bébé (foie en danger)'] },
      { n: 4, title: 'Cause suspectée',
        why: 'Aide à anticiper récidive et nature de l\'obstacle.',
        how: ['« Que mangeait-il / portait-il en bouche ? »', '« Quand cela a commencé ? »'],
        pitfalls: ['Perdre du temps si manœuvre urgente'] },
    ],
    citizen_behavior: {
      title: 'Guider Heimlich par téléphone',
      tone: ['Voix urgente mais structurée', 'Donner instructions étape par étape', 'Recompter à voix haute'],
      phrases_utiles: ['« Placez-vous derrière lui, vos bras autour de son ventre. »', '« Poing juste au-dessus du nombril, autre main par-dessus. »', '« Tirez fort vers vous et vers le haut. »', '« Recommencez jusqu\'à ce que l\'objet sorte ou qu\'il perde conscience. »'],
      phrases_a_eviter: ['« Tapez dans le dos d\'un adulte qui s\'étouffe » (peut enfoncer l\'objet)', '« Donnez à boire de l\'eau » (peut aggraver)', '« Mettez vos doigts dans la gorge à l\'aveugle »'],
      panique_gestion: ['Si témoin paralysé par peur → instructions très simples', 'Si patient devient inconscient → l\'allonger, commencer RCR', 'Si bébé qui s\'étouffe → tapes dorsales, jamais Heimlich'],
    },
    red_flags: ['Silence complet (aucun son)', 'Cyanose des lèvres', 'Signe universel (mains au cou)', 'Perte de conscience', 'Tentative inefficace de tousser', 'Bébé qui devient flasque', 'Antécédent d\'épisode récidivant'],
    pre_arrival: [
      'Si tousse encore → encourager à tousser fort',
      'Si silence + cyanose → manœuvre de Heimlich immédiate',
      'Adulte/enfant > 1 an : Heimlich abdominal',
      'Bébé < 1 an : 5 tapes dorsales + 5 compressions thoraciques (alternance)',
      'Si inconscience → RCR avec vérification bouche entre chaque cycle',
      'Femme enceinte / obèse → compressions thoraciques au lieu d\'abdominales',
      'Ne jamais mettre les doigts à l\'aveugle dans la bouche',
      'Si objet visible → tenter de l\'extraire avec doigt en crochet',
    ],
    common_errors: [
      'Heimlich sur obstruction partielle',
      'Heimlich sur bébé',
      'Mettre doigts à l\'aveugle',
      'Donner à boire pour "faire passer"',
      'Sous-estimer un bébé silencieux',
    ],
    discussion_questions: [
      'Enfant de 2 ans devient silencieux après avoir mangé un raisin. Action ?',
      'Bébé 6 mois qui devient flasque après tétée. Conduite ?',
      'Femme enceinte 8 mois en obstruction complète. Adaptation ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 11 — Choking',
      'AHA — BLS Choking Algorithm',
      'Heart & Stroke Foundation — Basic Life Support',
    ],
  },

  noyade: {
    title: 'Noyade',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `La noyade est un processus, pas un événement. Elle peut se produire en quelques cm d\'eau et sans cri (les enfants qui se noient ne crient pas). Toute personne sortie de l\'eau ayant inhalé doit être surveillée à l\'hôpital — la « noyade secondaire » peut survenir des heures plus tard par œdème pulmonaire.`,
    stats: [
      { icon: Heart, text: '~500 noyades/an au Canada, 80 % en eau libre.' },
      { icon: AlertTriangle, text: 'Les enfants de 1-4 ans se noient en quelques cm d\'eau, en silence.' },
      { icon: Clock, text: 'La noyade secondaire peut survenir 1 à 72 h après inhalation.' },
    ],
    objectives: [
      'Sécuriser le sauveteur AVANT toute intervention.',
      'Initier la RCR avec ventilations PRIVILÉGIÉES (manque d\'oxygène premier).',
      'Suspecter trauma rachidien si plongeon en eau peu profonde.',
      'Reconnaître la noyade secondaire (détérioration retardée).',
      'Prévenir l\'hypothermie.',
    ],
    phases: [
      { n: 2, title: 'Sécurité du sauveteur',
        why: 'Le risque #1 = sauveteur qui se noie.',
        how: ['« Où est la victime — encore dans l\'eau ou sortie ? »', '« NE plongez PAS pour la sauver si vous n\'êtes pas formé. »', '« Tendez perche, lance bouée flottante, mais ne nagez PAS vers elle. »'],
        pitfalls: ['Encourager sauvetage par non-formé'] },
      { n: 3, title: 'État après sortie',
        why: 'Détermine RCR ou surveillance.',
        how: ['« Respire-t-il ? »', '« Est-il conscient ? »', '« Combien de temps sous l\'eau ? »', '« Eau douce ou salée ? Eau froide ? »'],
        pitfalls: ['Sous-estimer une noyade "récupérée" rapidement'] },
      { n: 4, title: 'Mécanisme',
        why: 'Trauma rachidien possible si plongeon, accident.',
        how: ['« A-t-il plongé en eau peu profonde ? »', '« Accident de bateau, chute ? »', '« Convulsions ou perte de conscience dans l\'eau ? »'],
        pitfalls: ['Mobiliser sans précaution si trauma'] },
    ],
    citizen_behavior: {
      title: 'Sortir et stabiliser une victime',
      tone: ['Voix urgente sur sécurité du sauveteur', 'Directives claires pour la RCR', 'Insister sur la prévention hypothermie'],
      phrases_utiles: ['« Ne plongez PAS si vous n\'êtes pas formé. »', '« Tendez une perche, lancez une bouée. »', '« Une fois sortie, allongez-la sur le dos, je vous guide. »', '« Couvrez-la avec une couverture sèche. »'],
      phrases_a_eviter: ['« Plongez pour la chercher »', '« Mettez la tête en bas pour vider l\'eau » (perte de temps, peut aspirer)', '« Donnez à boire pour réchauffer »'],
      panique_gestion: ['Si témoin veut absolument plonger → expliquer le risque de 2 noyés', 'Si enfant noyé → parents souvent paralysés, instructions ultra simples', 'Si en attente des secours → couverture + maintien au chaud'],
    },
    red_flags: ['Sous l\'eau > 1 min', 'Cyanose / coloration grise', 'Inconscience', 'Pas de respiration ou respiration agonale', 'Eau très froide (hypothermie)', 'Plongeon en eau peu profonde (trauma rachis)', 'Convulsions dans l\'eau', 'Antécédent cardiaque', 'Enfant sans surveillance'],
    pre_arrival: [
      'Sécuriser le sauveteur en priorité',
      'Sortir la victime avec précautions rachidiennes si trauma suspecté',
      'NE PAS tenter de vider l\'eau des poumons',
      'Si arrêt → RCR avec 5 ventilations initiales avant compressions',
      'Couvrir d\'une couverture sèche (hypothermie)',
      'Surveiller respiration et conscience',
      'Surveillance hospitalière obligatoire même si "récupérée"',
      'Hypothermie sévère → RCR prolongée possible (« pas mort tant que pas chaud »)',
    ],
    common_errors: [
      'Plonger pour sauver sans formation',
      'Tenter de vider l\'eau (mythe)',
      'Sous-estimer une noyade "récupérée"',
      'Oublier la prévention de l\'hypothermie',
      'Ne pas suspecter trauma rachis après plongeon',
    ],
    discussion_questions: [
      'Enfant retrouvé inconscient au bord de la piscine. Conduite ?',
      'Adolescent ayant plongé en eau peu profonde, conscient mais ne peut bouger jambes. Action ?',
      'Patient sorti vivant après 20 min en eau glacée. Pronostic ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 14 — Drowning/Diving',
      'Société de sauvetage du Québec',
      'ILS — International Life Saving Federation Guidelines',
    ],
  },

});
